import clsx from "clsx";
import React, { MouseEvent, ReactNode, useCallback, useState } from "react";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { PaginationProgressBar } from "web/react/components/pagination-progress-bar/pagination-progress-bar";
import { ArchiveDeviceDivider } from "web/react/emo/archive-device-divider";
import { Button } from "web/react/emo/button";
import { Sprinkles, sprinkles } from "web/react/emo/sprinkles.css";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useRothkoFetcher } from "web/react/hooks/use-rothko-fetcher";
import { gettext } from "web/script/modules/django-i18n";
import history from "web/script/utils/history";
import * as styles from "./paginated-content.css";

export interface PaginatedFeedMeta {
    page_size: number;
    current_page: number;
    next_page: number | null;
    total: number;
}

interface PaginatedFeedProps<Item> {
    items: Item[];
    meta: PaginatedFeedMeta;
    // rothko module path used to fetch the next page (e.g. "modules/lyst_news")
    rothkoModule: string;
    // optional side effect (e.g. analytics) fired when "show more" is clicked
    onLoadMore?: () => void;
    // optional background colour token; defaults to no background (white)
    backgroundColor?: Sprinkles["backgroundColor"];
    // renders the current page of items
    children: (items: Item[]) => ReactNode;
}

function getNextPage(next_page: number): URL {
    const url = new URL(global.window.location.href);
    url.searchParams.set("page", next_page.toString());

    return url;
}

export function PaginatedContent<Item>({
    items,
    meta,
    rothkoModule,
    onLoadMore,
    backgroundColor,
    children,
}: PaginatedFeedProps<Item>): React.ReactElement {
    const { isDomLoaded } = useDomViewport();

    const [itemsOnPage, setItemsOnPage] = useState(items);
    const [pageMeta, setPageMeta] = useState(meta);
    const [fetch, isLoading] = useRothkoFetcher<{ items: Item[]; meta: PaginatedFeedMeta }>(
        rothkoModule,
        undefined,
        true
    );

    const onButtonClick = useCallback(
        async function onButtonClick(
            event: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLAnchorElement>
        ): Promise<void> {
            event.preventDefault();
            // don't fetch if there is no next page
            // we should never land here
            if (!pageMeta.next_page) {
                return;
            }

            onLoadMore?.();

            const newContent = await fetch({ page: pageMeta.next_page.toString() });

            if (newContent && isDomLoaded) {
                // Before we update the state, set the url param with the
                // "next_page" we just used to fetch.
                // Then update URL without reloading.
                // (see media/web/script/components/lyst-app.js)
                const url = getNextPage(pageMeta.next_page);
                history.pushState(url, { saveScrollPosition: false });

                // add the new content to the already existing content
                setItemsOnPage([...itemsOnPage, ...newContent.items]);

                // update pagination details
                setPageMeta(newContent.meta);
            }
        },
        [itemsOnPage, pageMeta, fetch, isDomLoaded, onLoadMore]
    );

    // "Showing {current_product_count} of {total_product_count}"
    const paginationText =
        pageMeta.total > 0 || itemsOnPage.length === 0
            ? gettext("feed_pagination.summary_showing_products", {
                  current_product_count: itemsOnPage.length,
                  total_product_count: pageMeta.total,
              })
            : "";

    const nextPageURL = isDomLoaded && pageMeta.next_page ? getNextPage(pageMeta.next_page) : null;

    return (
        <div className={clsx(styles.wrapper, backgroundColor && sprinkles({ backgroundColor }))}>
            {children(itemsOnPage)}
            {itemsOnPage.length > 0 ? (
                <VStack align="center" justify="center" spacing="none">
                    <View paddingBottom="xs">
                        <Text textStyle="body-s-bold">{paginationText}</Text>
                    </View>
                    <div className={styles.progressionWrapper}>
                        <PaginationProgressBar
                            currentNumber={itemsOnPage.length}
                            maximumNumber={pageMeta.total}
                        />
                    </div>
                    {nextPageURL && (
                        <Button
                            title={gettext("general.show_more")}
                            variant={"secondary"}
                            width={"full"}
                            onClick={onButtonClick}
                            href={nextPageURL.href}
                        />
                    )}
                    <div className={clsx(styles.loadingWrapper, styles.inlineLoadingWrapper)}>
                        {isLoading && <LoadingSpinner />}
                    </div>
                </VStack>
            ) : (
                gettext("retailer_promo_profile.feed_empty_text")
            )}
            <ArchiveDeviceDivider />
        </div>
    );
}
